import { Link } from "react-router-dom";
import { Gauge, Calendar, Cog } from "lucide-react";
import { fileUrl } from "@/lib/api";

const placeholder =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=800&q=80";

export const VehicleCard = ({ vehicle, index }) => {
  const mainPath = vehicle.photos?.[vehicle.main_photo_index] || vehicle.photos?.[0];
  const img = mainPath ? fileUrl(mainPath) : placeholder;
  const isSold = vehicle.status === "sold";
  const isComingSoon = vehicle.status === "coming_soon";
  const isNew =
    !isSold &&
    !isComingSoon &&
    vehicle.show_just_arrived !== false &&
    vehicle.created_at &&
    Date.now() - new Date(vehicle.created_at).getTime() < 14 * 24 * 3600 * 1000;

  const cardClass =
    "group bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all duration-200 flex flex-col animate-fade-up" +
    (isComingSoon ? " opacity-95" : " hover:-translate-y-1 hover:shadow-xl");

  const inner = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img
          src={img}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          loading="lazy"
          className={
            "w-full h-full object-cover transition-transform duration-300" +
            (isComingSoon ? "" : " group-hover:scale-105")
          }
        />
        {isSold ? (
          <span
            className="absolute top-3 left-3 bg-rose-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-lg"
            data-testid={`vehicle-card-sold-badge-${vehicle.id}`}
          >
            Sold
          </span>
        ) : isComingSoon ? (
          <span
            className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-lg"
            data-testid={`vehicle-card-coming-soon-badge-${vehicle.id}`}
          >
            Coming Soon
          </span>
        ) : isNew ? (
          <span
            className="absolute top-3 left-3 bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-lg"
            data-testid={`vehicle-card-new-badge-${vehicle.id}`}
          >
            Just Arrived
          </span>
        ) : null}
        <span className="absolute bottom-3 right-3 bg-slate-900/90 text-white font-black px-3 py-1 rounded-lg backdrop-blur-sm text-lg">
          {isComingSoon ? (
            <span className="text-sm font-bold">Call for details!</span>
          ) : (
            `$${vehicle.price.toLocaleString()}`
          )}
        </span>
      </div>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <h3 className="font-display font-bold text-slate-900 text-lg leading-snug">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </h3>
          <p className="text-xs uppercase tracking-wider text-slate-400 mt-0.5">
            {vehicle.body_type}
          </p>
        </div>
        <div className="mt-auto flex items-center gap-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
          <span className="flex items-center gap-1">
            <Gauge size={14} /> {vehicle.mileage.toLocaleString()} km
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={14} /> {vehicle.year}
          </span>
          {vehicle.transmission && (
            <span className="flex items-center gap-1">
              <Cog size={14} /> {vehicle.transmission}
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (isComingSoon) {
    return (
      <div
        className={cardClass}
        style={{ animationDelay: `${(index % 8) * 60}ms` }}
        data-testid={`vehicle-card-${vehicle.id}`}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={`/vehicle/${vehicle.id}`}
      className={cardClass}
      style={{ animationDelay: `${(index % 8) * 60}ms` }}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      {inner}
    </Link>
  );
};
